import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';

import { useEntertainerEngagementContext } from '@contexts/EntertainerEngagementContext';
import pluralize from 'pluralize';

import API from '@api/API';

import {
  CurrentEntertainer,
  useCurrentEntertainerQuery,
  useFetchCurrentEntertainer,
} from '@queries/EntertainerQueries';
import { FeedEvent } from '@queries/EventQueries';

import { useAddEntertainerToEventMutation } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCityById } from '@hooks/useCurrentUserLocation';

import { EntertainerDashboardAction, useNavigate, useRouter } from '@router/index';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';

interface ScheduledSocialPost {
  title: string;
  primaryImageSlideUrl: string;
  events: FeedEvent[];
}

const useScheduledSocialPostByCityId = (cityId: string) => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: ['dragKingCommunitySubmissions', cityId],
    queryFn: () =>
      API.get<{ scheduledSocialPost: ScheduledSocialPost }>(
        `/v1/cities/${cityId}/event-social-post`,
      ),
    enabled: !!cityId,
  });

  return {
    scheduledSocialPost: data?.scheduledSocialPost,
    isScheduledSocialPostReady: isFetched,
    refetchScheduledSocialPost: refetch,
  };
};

export const ScheduledSocialPostTemplate: FC<{
  cardTitle: string;
  imageUrl: string;
  eventTitle: string;
  eventDetails: string;
  button?: {
    text: string;
    action: () => void;
  };
}> = ({ cardTitle, imageUrl, eventTitle, eventDetails, button }) => {
  return (
    <div className="p-2" style={{ borderRadius: 8, backgroundColor: 'rgba(255,255,255,.2)' }}>
      <div className="mb-2">{cardTitle}</div>
      <img
        src={imageUrl}
        alt={eventTitle}
        style={{ width: 'auto', height: 240, borderRadius: 4 }}
      />
      <div>
        <div style={{ marginBottom: -4 }}>
          <strong>{eventTitle}</strong>
        </div>
        {eventDetails && (
          <div>
            <span style={{ fontSize: '.7rem' }}>{eventDetails}</span>
          </div>
        )}
        {button && (
          <div className="pt-1">
            <Button
              variant="smooth"
              color="neutral"
              size="sm"
              onClick={button.action}
              style={{ borderRadius: 4 }}
            >
              {button.text}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const ScheduledSocialPost: FC<{
  hideIndividualFeatureTitle?: boolean;
  cityId: string;
  description?: string;
  castOverride?: {
    action: (eventId: string) => void;
    text: string;
  };
}> = ({ hideIndividualFeatureTitle, cityId, description, castOverride }) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();
  const { scheduledSocialPost, refetchScheduledSocialPost } =
    useScheduledSocialPostByCityId(cityId);
  const { openGatedAddShowPopup } = useEntertainerEngagementContext();
  const { city } = useCityById(cityId);
  const { openConfirmDialog } = useDialog();
  const { createAuthGateHandler } = useAuthGate();
  const { entertainer } = useCurrentEntertainerQuery();
  const fetchCurrentEntertainer = useFetchCurrentEntertainer();
  const { addEntertainerToEventAsync } = useAddEntertainerToEventMutation();

  if (!scheduledSocialPost) {
    return <></>;
  }

  const slideCount = scheduledSocialPost.events.length + 1;

  const handleAddFlyer = () => {
    track('Add IG Post Flyer');
    openGatedAddShowPopup({
      cityId,
      isEntertainerOptional: true,
      callback: refetchScheduledSocialPost,
    });
  };

  const handleJoinCast = (
    eventId: string,
    cb: (createdEntertainer: Pick<CurrentEntertainer, '_id' | 'title'>) => void,
  ) => {
    track('Join Cast', { eventId });
    openConfirmDialog({
      title: 'Join Cast?',
      text: "You're about to add yourself to the cast list for this show. This won't book you or change the show's lineup—it just helps keep the info accurate. Continue?",
      onConfirm: () => {
        createAuthGateHandler(
          async () => {
            let addedEntertainer = entertainer;

            if (!addedEntertainer?._id) {
              const { entertainer: refetchedEntertainer } = await fetchCurrentEntertainer();
              addedEntertainer = refetchedEntertainer;
            }

            await addEntertainerToEventAsync({ entertainerId: addedEntertainer?._id, eventId });
            await cb(addedEntertainer);
          },
          {
            entertainerCallback: () => {},
            cityId,
          },
        );
      },
      buttonOk: 'Join',
    });
  };

  const DEFAULT_DESCRIPTION = `We're spotlighting ${city?.name} shows that feature drag kings! Add your event to get reshared on Instagram.`;

  return (
    <>
      <TitleToolbar
        text={
          <span>
            <FontAwesomeIcon name="instagram" faStyle="fab" />
            <span className="ps-1">{`Upcoming ${city?.name} Post`}</span>
          </span>
        }
        className="mb-1"
        size="md"
        action={<span>{`${slideCount} ${pluralize('slide', slideCount)}`}</span>}
      />

      <p className="mt-0">{description ?? DEFAULT_DESCRIPTION}</p>

      <HorizontalScroll
        items={[
          <div className="d-flex gap-1 pe-4">
            <ScheduledSocialPostTemplate
              cardTitle="Slide 1"
              imageUrl={scheduledSocialPost.primaryImageSlideUrl}
              eventTitle={scheduledSocialPost.title}
              eventDetails={'--'}
              button={{
                text: 'Add Flyer',
                action: handleAddFlyer,
              }}
            />
            {scheduledSocialPost.events.map((event, i) => (
              <ScheduledSocialPostTemplate
                key={`event-${event._id}`}
                cardTitle={`Slide ${i + 2}`}
                imageUrl={event.img}
                eventTitle={event.title}
                eventDetails={event.timeDetails}
                button={
                  castOverride
                    ? { text: castOverride.text, action: () => castOverride.action(event._id) }
                    : {
                        text: "I'm in the cast",
                        action: () => handleJoinCast(event._id, () => {}),
                      }
                }
              />
            ))}
            <div
              className="p-2"
              style={{ borderRadius: 8, width: 260, backgroundColor: 'rgba(255,255,255,.2)' }}
            >
              <div className="mb-2">Add Flyer</div>
              <div
                className="d-flex align-items-center justify-content-center text-center"
                style={{
                  borderRadius: 4,
                  backgroundColor: 'rgba(0,0,0,.3)',
                  height: 240,
                }}
                onClick={handleAddFlyer}
              >
                <div>
                  <div className="fs-1">
                    <FontAwesomeIcon name="image" />
                  </div>
                  <div>Upload flyer</div>
                </div>
              </div>
            </div>
          </div>,
        ]}
      />

      <div className="mb-4" />

      {!entertainer?._id && !hideIndividualFeatureTitle && (
        <div className="text-align-center fs-6">
          <strong>⬇️&nbsp;&nbsp;Looking for an individual feature?&nbsp;&nbsp;⬇️</strong>
        </div>
      )}
    </>
  );
};
